import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Award, Download, FileText, TrendingUp, AlertCircle } from "lucide-react";
import jsPDF from "jspdf";

export default function EducationBulletins() {
  const [eleves, setEleves] = useState([]);
  const [picked, setPicked] = useState("");
  const [bulletin, setBulletin] = useState(null);

  useEffect(() => {
    api.get("/chef/education/eleves").then((r) => setEleves(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!picked) { setBulletin(null); return; }
    api.get(`/chef/education/bulletin/${picked}`).then((r) => setBulletin(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [picked]);

  const downloadPDF = () => {
    if (!bulletin) return;
    const doc = new jsPDF();
    const W = 210;
    const M = 15;
    let y = M;

    doc.setFillColor(10, 10, 10);
    doc.rect(0, 0, W, 30, "F");
    doc.setTextColor(255, 215, 0);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("// PAG ÉDUCATION", M, 10);
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.text("BULLETIN SCOLAIRE", M, 22);

    y = 40;
    doc.setTextColor(10, 10, 10);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(`${bulletin.eleve.prenom} ${bulletin.eleve.nom}`, M, y);
    y += 6;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Classe : ${bulletin.eleve.classe_nom || "—"}`, M, y);
    y += 5;
    if (bulletin.eleve.date_naissance) {
      doc.text(`Né(e) le : ${new Date(bulletin.eleve.date_naissance).toLocaleDateString("fr-FR")}`, M, y);
      y += 5;
    }
    y += 5;

    // Moyennes
    doc.setFillColor(0, 47, 167);
    doc.rect(M, y, W - 2 * M, 7, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("MATIÈRE", M + 3, y + 5);
    doc.text("NB NOTES", M + 80, y + 5);
    doc.text("MOYENNE / 20", W - M - 30, y + 5);
    y += 10;

    doc.setTextColor(10, 10, 10);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    bulletin.moyennes_par_matiere.forEach((m, i) => {
      doc.setFillColor(248, 249, 250);
      if (i % 2 === 0) doc.rect(M, y - 4, W - 2 * M, 7, "F");
      doc.text(m.matiere, M + 3, y);
      doc.text(String(m.nb_notes), M + 80, y);
      doc.setFont("helvetica", "bold");
      doc.text(`${m.moyenne_20.toFixed(2)}`, W - M - 25, y);
      doc.setFont("helvetica", "normal");
      y += 7;
    });

    y += 5;
    doc.setFillColor(255, 215, 0);
    doc.rect(M, y, W - 2 * M, 10, "F");
    doc.setTextColor(10, 10, 10);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(`MOYENNE GÉNÉRALE : ${bulletin.moyenne_generale.toFixed(2)} / 20`, M + 3, y + 7);
    y += 18;

    // Absences
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Absences : ${bulletin.nb_absences}    Retards : ${bulletin.nb_retards}    Notes totales : ${bulletin.nb_notes}`, M, y);
    y += 15;

    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.text(`Édité le ${new Date().toLocaleDateString("fr-FR")} via PAG Éducation`, M, 285);

    doc.save(`bulletin-${bulletin.eleve.nom}-${bulletin.eleve.prenom}.pdf`);
    toast.success("Bulletin téléchargé");
  };

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow="Éducation · Bulletins"
        title="Bulletins scolaires"
        sub="Sélectionnez un élève pour voir ses moyennes et télécharger son bulletin."
        right={
          <Select value={picked} onValueChange={setPicked}>
            <SelectTrigger data-testid="bulletin-picker" className="h-11 w-64"><SelectValue placeholder="Choisir un élève…" /></SelectTrigger>
            <SelectContent>
              {eleves.map((e) => <SelectItem key={e.id} value={e.id}>{e.prenom} {e.nom}</SelectItem>)}
            </SelectContent>
          </Select>
        }
      />
      <div className="p-6 lg:p-10">
        {!bulletin ? (
          <div className="bg-white border border-[#DEE2E6] p-10 text-center text-sm text-[#868E96]" data-testid="bulletin-empty">
            <Award className="h-10 w-10 mx-auto mb-3 text-[#DEE2E6]" />
            Sélectionnez un élève dans la liste.
          </div>
        ) : (
          <div className="space-y-6" data-testid="bulletin-content">
            {/* Header card */}
            <div className="bg-[#0A0A0A] text-white p-6 flex items-start justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFD700] font-bold">// Bulletin scolaire</div>
                <h2 className="font-display text-3xl font-black mt-2" data-testid="bulletin-eleve-name">{bulletin.eleve.prenom} {bulletin.eleve.nom}</h2>
                <div className="text-sm text-white/70 mt-1">Classe : {bulletin.eleve.classe_nom || "—"}</div>
              </div>
              <Button onClick={downloadPDF} data-testid="bulletin-download-btn" className="bg-[#FFD700] text-[#0A0A0A] hover:bg-white"><Download className="h-4 w-4 mr-2" /> Télécharger PDF</Button>
            </div>

            {/* Moyenne générale */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-0 border border-[#0A0A0A]">
              <div className="p-6 bg-[#FFD700]" data-testid="moyenne-generale">
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold">Moyenne générale</div>
                <div className="font-display text-5xl font-black mt-2">{bulletin.moyenne_generale.toFixed(2)}<span className="text-2xl"> / 20</span></div>
              </div>
              <KPI label="Notes" value={bulletin.nb_notes} icon={Award} />
              <KPI label="Absences" value={bulletin.nb_absences} icon={AlertCircle} warn={bulletin.nb_absences > 0} />
              <KPI label="Retards" value={bulletin.nb_retards} icon={TrendingUp} warn={bulletin.nb_retards > 0} />
            </div>

            {/* Détail par matière */}
            <div className="bg-white border border-[#DEE2E6]">
              <div className="grid grid-cols-12 px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-[#868E96] font-bold border-b border-[#DEE2E6]">
                <div className="col-span-6">Matière</div>
                <div className="col-span-3 text-center">Notes</div>
                <div className="col-span-3 text-right">Moyenne / 20</div>
              </div>
              {bulletin.moyennes_par_matiere.length === 0 ? (
                <div className="p-8 text-sm text-[#868E96] text-center">Aucune note enregistrée pour cet élève.</div>
              ) : bulletin.moyennes_par_matiere.map((m) => {
                const color = m.moyenne_20 >= 14 ? "#059669" : m.moyenne_20 >= 10 ? "#002FA7" : "#E60000";
                return (
                  <div key={m.matiere} className="grid grid-cols-12 px-5 py-3 items-center border-b border-[#F8F9FA] last:border-b-0" data-testid={`bulletin-row-${m.matiere}`}>
                    <div className="col-span-6 font-bold">{m.matiere}</div>
                    <div className="col-span-3 text-center text-sm">{m.nb_notes} note{m.nb_notes > 1 ? "s" : ""}</div>
                    <div className="col-span-3 text-right font-display text-2xl font-black" style={{ color }}>{m.moyenne_20.toFixed(2)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function KPI({ label, value, icon: Icon, warn }) {
  return (
    <div className={`p-6 ${warn ? "bg-[#FEF2F2]" : "bg-white"} border-l border-[#0A0A0A]`}>
      <div className="flex items-center gap-2"><Icon className={`h-3 w-3 ${warn ? "text-[#E60000]" : "text-[#002FA7]"}`} /><div className="text-[10px] uppercase tracking-[0.25em] font-bold">{label}</div></div>
      <div className={`font-display text-3xl font-black mt-2 ${warn ? "text-[#E60000]" : ""}`}>{value}</div>
    </div>
  );
}
